import { Dispatch, FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Action } from 'redux';

import lang from '../../lib/language';
import { EpicMoversTabEnum } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getWeek52HighLowTopList as getWeek52HighLowTopListCall } from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { isCallStatusError, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import NoDataAvailable from '../../components/NoDataAvailable/NoDataAvailable';
import { useIsMobile } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../util/routes';

import Week52GainersAndLosersList from './components/Week52GainersAndLosersList/Week52GainersAndLosersList';

import styles from './Week52GainersAndLosers.module.scss';

const Week52GainersAndLosers: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    getWeek52HighLowTopList,
    isWeek52HighLowTopListReady,
    isWeek52HighLowTopListError,
    week52HighLowTopList,
  } = props;
  const { topNHigh, topNLow } = week52HighLowTopList;
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<EpicMoversTabEnum>(EpicMoversTabEnum.TopGainers);
  const isTopGainersSelected = selectedTab === EpicMoversTabEnum.TopGainers;
  const isTopLoserSelected = selectedTab === EpicMoversTabEnum.TopLosers;
  const hasHighData = topNHigh.length > 0;
  const hasLowData = topNLow.length > 0;

  useEffect(() => {
    getWeek52HighLowTopList();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const isRequestCompleted = isWeek52HighLowTopListReady || isWeek52HighLowTopListError;
  const hasNoAnyData = !hasHighData && !hasLowData && isRequestCompleted;
  return (
    <div className={isMobile ? styles.wrapperMobile : styles.wrapper}>
      {isMobile ? (
        <div className={styles.wrapperTitleMob}>
          <span className={styles.title}>
            {lang.commonWeek52GainersAndLosers()}
          </span>
          <span className={styles.subtitle}>
            {lang.commonWeek52GainersAndLosersStockMakingHighLow()}
          </span>
          {!hasNoAnyData && (
            <div className={styles.tabsWrapper}>
              <div className={styles.tabs}>

                <Button
                  className={isTopGainersSelected ? styles.tabActive : styles.tab}
                  disabled={!isRequestCompleted}
                  onClick={() => setSelectedTab(EpicMoversTabEnum.TopGainers)}
                >
                  {lang.commonEpicMoversGainers()}
                </Button>

                <Button
                  className={isTopLoserSelected ? styles.tabActive : styles.tab}
                  disabled={!isRequestCompleted}
                  onClick={() => setSelectedTab(EpicMoversTabEnum.TopLosers)}
                >
                  {lang.commonEpicMoversLosers()}
                </Button>

              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.wrapperTitle}>
          <div className={styles.left}>
            <span className={styles.title}>
              {lang.commonWeek52GainersAndLosers()}
            </span>
            <span className={styles.subtitle}>
              {lang.commonWeek52GainersAndLosersStockMakingHighLow()}
            </span>
          </div>
          <div className={styles.right}>
            <Button
              className={styles.viewAllBtn}
              disabled={!isRequestCompleted || hasNoAnyData}
              variant={ButtonTypesEnum.Link}
              onClick={() => {
                navigate(UI_ROUTES.discover + UI_ROUTES.fiftyTwoWeekHighLow, {
                  state: {
                    isTopGainersSelected,
                  },
                });
              }}
            >
              {lang.mobileFundsScreenViewAllBtn()}
            </Button>
          </div>
        </div>
      )}
      <div className={styles.listWrapper}>
        {hasNoAnyData
          ? (
            <NoDataAvailable
              className={styles.noDataToDisplay}
              isNoDataIcon
              title={lang.commonDiscover52WeekNoDataAll()}
            />
          )
          : (
            <div className={styles.list}>
              {(!isMobile || isTopGainersSelected)
                && (
                  <Week52GainersAndLosersList
                    isReduction={false}
                    title={lang.commonEpicMoversGainers()}
                    data={topNHigh}
                    isTopGainersSelected
                  />
                )}
              {!isMobile && <div className={styles.verticalSpace} />}
              {(!isMobile || isTopLoserSelected)
                && (
                  <Week52GainersAndLosersList
                    isReduction
                    title={lang.commonEpicMoversLosers()}
                    data={topNLow}
                    isTopGainersSelected={false}
                  />
                )}
            </div>
          )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isWeek52HighLowTopListReady: isCallStatusReady(
    FundamentalCall.getWeek52HighLowTopList,
    state.fundamental.statusByCall,
  ),
  isWeek52HighLowTopListError: isCallStatusError(
    FundamentalCall.getWeek52HighLowTopList,
    state.fundamental.statusByCall,
  ),
  week52HighLowTopList: state.fundamental.week52HighLowTopList,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getWeek52HighLowTopList: () => dispatch(getWeek52HighLowTopListCall()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Week52GainersAndLosers);
